import React, { useState, useEffect, useRef } from 'react';
import QuestionSystem from './QuestionSystem';
import { db } from '../Firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import './JitsiMeet.css';
// [ลบออก] import Swal from 'sweetalert2';
// [ลบออก] import 'sweetalert2/dist/sweetalert2.min.css';

// ----------------------------------------------------------------
// Configuration
// ----------------------------------------------------------------
const JITSI_DOMAIN = '8x8.vc';
const TOKEN_API_URL = ''; // fallback if nothing found in firestore

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------
const loadJitsiScript = (domain, appId) => {
  return new Promise((resolve, reject) => {
    try {
      if (window.JitsiMeetExternalAPI) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = appId && appId.startsWith('vpaas-magic-cookie-')
        ? `https://${domain}/${appId}/external_api.js`
        : `https://${domain}/external_api.js`;
        
      script.async = true;
      script.onload = () => {
        if (window.JitsiMeetExternalAPI) resolve();
        else reject(new Error('Jitsi Meet External API not available after script load'));
      };
      script.onerror = (e) => reject(new Error('Failed to load Jitsi script: ' + (e?.message || e)));
      document.body.appendChild(script);
    } catch (err) {
      reject(err);
    }
  });
};

const boolLikeToTrueFalse = (v) => {
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (v === null || typeof v === 'undefined') return '';
  const s = String(v).trim().toLowerCase();
  if (s === 'true' || s === '1' || s === 'yes') return 'true';
  if (s === 'false' || s === '0' || s === 'no') return 'false';
  return s ? (s === 'true' ? 'true' : s) : '';
};

const getAppIdFromRoomName = (roomName) => {
  try {
    if (!roomName) return '';
    const parts = roomName.split('/');
    if (parts.length > 0 && parts[0].startsWith('vpaas-magic-cookie-')) {
      return parts[0];
    }
  } catch (_) {}
  return '';
};

const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1]; // เอาส่วน Payload
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to parse JWT", e);
    return null; // คืนค่า null ถ้าถอดรหัสล้มเหลว
  }
};

// ----------------------------------------------------------------
// Component
// ----------------------------------------------------------------
const JitsiMeet = ({ meetLink, onExit, showCountdown, countdown, userId, userRole, jwtToken, userName }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meetingInfo, setMeetingInfo] = useState({ title: '', roomName: '' });

  // DB-backed user values
  const [dbUserName, setDbUserName] = useState('');
  const [dbModulator, setDbModulator] = useState('');
  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);

  const jitsiContainerRef = useRef(null);
  const apiRef = useRef(null);

  const updateMeetingInfo = (updates) => setMeetingInfo(prev => ({ ...prev, ...updates }));

  // [แก้ไข] ค้นหาด้วย userName ที่ถูกต้อง (nathadaj) แทน userId (user-21h5st)
  const resolveUserDoc = async () => {
    
    const idToSearch = userName || userId; // <--- ใช้ userName ก่อน
    if (!idToSearch) return null;

    try {
      const uRef = doc(db, 'Competition-Training', idToSearch); // <--- ใช้ idToSearch
      const uSnap = await getDoc(uRef);
      if (uSnap.exists()) {
        return { id: idToSearch, data: uSnap.data() || {} };
      }
    } catch (e) {
      console.warn('[JitsiMeet] resolveUserDoc: read by idToSearch failed', idToSearch, e);
    }

    return null;
  };

  // ฟังก์ชันดึงข้อมูลผู้ใช้ (ไม่เปลี่ยนแปลง)
  const fetchUserDataForUI = async () => {
    try {
      const resolved = await resolveUserDoc();
      if (resolved) {
        const nameVal = resolved.data?.name ? String(resolved.data.name).trim() : '';
        const modVal = (typeof resolved.data?.Modulator !== 'undefined' && resolved.data?.Modulator !== null) ? String(resolved.data.Modulator).trim() : '';
        setDbUserName(nameVal);
        setDbModulator(modVal);
      } else {
        setDbUserName('');
        setDbModulator('');
      }
    } catch (e) {
      console.error('[JitsiMeet] fetchUserDataForUI error', e);
      setDbUserName('');
      setDbModulator('');
    } finally {
      setIsUserDataLoaded(true);
    }
  };

  useEffect(() => {
    setIsUserDataLoaded(false);
    fetchUserDataForUI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userName]);


  /**
   * ฟังก์ชันนี้จะคืนค่าเป็น Object ที่มีข้อมูลที่จำเป็นทั้งหมด
   * @param {string} originalRoomName - ชื่อห้องเดิมที่ได้จาก meetLink
   * @param {string} passedDbUserName - ชื่อผู้ใช้ที่ดึงจาก Firestore
   ** @param {string} passedDbModulator - สถานะ Modulator ที่ดึงจาก Firestore
   * @returns {Promise<{token: string|null, roomName: string, appId: string}>}
   */
  const fetchJitsiToken = async (originalRoomName, passedDbUserName, passedDbModulator) => {
    
    const appIdFromRequestedRoom = getAppIdFromRoomName(originalRoomName);

    // --- กรณีที่ 1: ใช้ JWT Token จาก Props ---
    if (jwtToken) {
      return {
        token: jwtToken,
        roomName: originalRoomName,
        appId: appIdFromRequestedRoom
      };
    }

    // --- กรณีที่ 2: ค้นหา API URL จาก Firestore ---
    
    // [แก้ไข] resolveApiUrl จะคืนค่า { finalUrl, roomName, appId }
    const resolveApiUrl = async () => {
      // ใช้ข้อมูลผู้ใช้ที่ส่งผ่านมา
      const loggedUserName = passedDbUserName || '';
      const loggedUserMod = passedDbModulator || ''; 

      // 1. ตรวจสอบ Admin Room
      try {
        const adminRoomRef = doc(db, 'Competition-Training', 'nathadaj', 'Admin', 'Room');
        const adminRoomSnap = await getDoc(adminRoomRef);
        if (adminRoomSnap.exists()) {
          const adminRoomData = adminRoomSnap.data() || {};
          const meetingFromAdminRoom = adminRoomData?.Meeting ? String(adminRoomData.Meeting).trim() : ''; 
          if (meetingFromAdminRoom) {
            try {
              const roomAdminRef = doc(db, 'Competition-Training', 'nathadaj', meetingFromAdminRoom, 'Admin');
              const roomAdminSnap = await getDoc(roomAdminRef);
              if (roomAdminSnap.exists()) {
                const roomAdminData = roomAdminSnap.data() || {};
                const baseApi = roomAdminData?.API ? String(roomAdminData.API).trim() : '';
                if (baseApi) {
                  const finalName = loggedUserName || (roomAdminData?.name ? String(roomAdminData.name).trim() : '') || userName || userId || 'Guest';
                  const finalModRaw = loggedUserMod;
                  const finalMod = boolLikeToTrueFalse(finalModRaw) || 'false'; 

                  const finalUrl = `${baseApi}?room=${encodeURIComponent(meetingFromAdminRoom)}&name=${encodeURIComponent(finalName)}&mod=${encodeURIComponent(finalMod)}`;
                  const appIdForThisFinal = getAppIdFromRoomName(meetingFromAdminRoom);

                  return {
                    finalUrl,
                    roomName: meetingFromAdminRoom,
                    appId: appIdForThisFinal 
                  };
                }
              }
            } catch (e) {
              console.error('[JitsiMeet] Error reading room admin doc:', e);
            }
          }
        }
      } catch (e) {
        console.error('[JitsiMeet] Error reading Admin/Room doc:', e);
      }

      // 2. ตรวจสอบ Candidate paths
      let roomKey = '';
      const m = /([0-9]+)/.exec(originalRoomName || ''); // ใช้ originalRoomName ในการค้นหา
      if (m && m[1]) roomKey = `Room${m[1]}`;
      if (!roomKey) roomKey = 'Room1';

      const candidatePaths = [
        ['Competition-Training', 'nathadaj', roomKey, 'Admin'],
        ['Competition-Training', 'nathadaj', `PowerWisdom@ห้องสอบ${m && m[1] ? m[1] : '1'}`, 'Admin-Room1'],
        ['Competition-Training', 'nathadaj', 'Admin', roomKey],
      ];

      for (const pathArr of candidatePaths) {
        try {
          const ref = doc(db, ...pathArr);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data() || {};
            if (data && typeof data.API === 'string' && data.API.trim().length > 0) {
              const baseApi = data.API.trim();

              const finalRoomNameFromDoc = data.Meeting ? String(data.Meeting).trim() : roomKey;
              const finalName = loggedUserName || (data.name ? String(data.name).trim() : '') || userName || userId || 'Guest';
              const finalModRaw = loggedUserMod;
              const finalMod = boolLikeToTrueFalse(finalModRaw) || 'false'; 

              const finalUrl = `${baseApi}?room=${encodeURIComponent(finalRoomNameFromDoc)}&name=${encodeURIComponent(finalName)}&mod=${encodeURIComponent(finalMod)}`;
              const appIdForThisFinal = getAppIdFromRoomName(finalRoomNameFromDoc);

              return {
                finalUrl,
                roomName: finalRoomNameFromDoc,
                appId: appIdForThisFinal 
              };
            }
          }
        } catch (e) {
          console.warn('[JitsiMeet] candidate path read error', pathArr.join('/'), e?.message || e);
        }
      }

      // 3. ใช้ Fallback TOKEN_API_URL (Logic เดิม)
      if (TOKEN_API_URL && String(TOKEN_API_URL).trim()) {
        return {
          finalUrl: TOKEN_API_URL,
          roomName: originalRoomName,
          appId: appIdFromRequestedRoom
        };
      }

      return null;
    }; // --- จบ resolveApiUrl ---

    // --- ส่วนหลักของ fetchJitsiToken ---
    try {
      const apiMeta = await resolveApiUrl();

      // ถ้าไม่พบ API URL เลย
      if (!apiMeta || !apiMeta.finalUrl) {
        // [แก้ไข] เปลี่ยนจาก Swal.fire เป็น setError
        setError('API URL ไม่ถูกตั้งค่า กรุณาติดต่อผู้ดูแล');
        return { token: null, roomName: originalRoomName, appId: appIdFromRequestedRoom };
      }

      let apiUrl = apiMeta.finalUrl;
      // ทำให้ URL สมบูรณ์
      try {
        const parsed = new URL(apiUrl);
        apiUrl = parsed.toString();
      } catch (_) {
        apiUrl = new URL(apiUrl.replace(/^\//, ''), window.location.origin + '/').toString();
      }

      // ==========================================================
      // [ลบออก] บล็อก SweetAlert ที่แสดง URL
      // ==========================================================
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      let response;
      let token = null;
      let tokenError = null;
      try {
        // เรียก API (Logic เดิม)
        response = await fetch(apiUrl, {
          method: 'GET',
          mode: 'cors',
          headers: { 'Accept': 'application/json' },
          signal: controller.signal,
        });

        if (response && response.ok) {
          try {
            const data = await response.json();
            const tokenCandidate = (typeof data === 'string') ? data : (data.jwt || data.token || null);
            if (tokenCandidate) {
              token = String(tokenCandidate).trim();
            }
          } catch (jsonErr) {
            try {
              const txt = await response.text();
              if (txt && txt.trim()) token = txt.trim();
            } catch (_) {}
          }
        } else {
          tokenError = response ? `${response.status} ${response.statusText}` : 'no response';
        }
      } catch (e) {
        console.error('[JitsiMeet] API call failed:', e);
        tokenError = e && e.name === 'AbortError' ? 'timeout' : (e?.message || 'fetch failed');
      } finally {
        clearTimeout(timeoutId);
      }

      return {
        token: token,
        roomName: apiMeta.roomName, // นี่คือ "ชื่อห้องแบบง่าย"
        appId: apiMeta.appId // นี่คือ '' หรือค่าจาก getAppIdFromRoomName
      };

    } catch (err) {
      console.error('[JitsiMeet] Error fetching JWT:', err);
      if (!jwtToken) {
        setError("ไม่สามารถเชื่อมต่อเพื่อขอ JWT จากเซิร์ฟเวอร์ได้ กรุณาตรวจสอบ Backend หรือ CORS และโครงสร้างการตอบกลับ JSON (ต้องมี jwt หรือ token)");
      }
      return { token: null, roomName: originalRoomName, appId: appIdFromRequestedRoom };
    }
  }; // --- จบ fetchJitsiToken ---

  // ----------------------------------------------------------------
  // ฟังก์ชันเริ่ม Jitsi (ไม่เปลี่ยนแปลง)
  // ----------------------------------------------------------------
  const startJitsiMeeting = async (originalRoomName, tokenOverride, passedDbUserName, passedDbModulator) => {
    try {
      let tokenResult;

      if (tokenOverride) {
        tokenResult = {
          token: tokenOverride,
          roomName: originalRoomName, 
          appId: getAppIdFromRoomName(originalRoomName)
        };
      } else {
        tokenResult = await fetchJitsiToken(originalRoomName, passedDbUserName, passedDbModulator);
      }

      const finalToken = tokenResult.token;
      const simpleRoomNameFromDb = tokenResult.roomName; 
      const appIdFromRoom = tokenResult.appId; 

      let appIdFromJwt = '';
      if (finalToken) {
        const payload = parseJwt(finalToken);
        if (payload && payload.sub && payload.sub.startsWith('vpaas-magic-cookie-')) {
          appIdFromJwt = payload.sub;
        }
      }

      const finalAppIdForScript = appIdFromJwt || appIdFromRoom;

      let finalRoomNameForJitsi;
      if (finalAppIdForScript && simpleRoomNameFromDb && !simpleRoomNameFromDb.startsWith('vpaas-magic-cookie-')) {
        finalRoomNameForJitsi = `${finalAppIdForScript}/${simpleRoomNameFromDb}`;
      } else {
        finalRoomNameForJitsi = simpleRoomNameFromDb;
      }
      
      if (finalToken === null && !TOKEN_API_URL && !jwtToken) { 
        console.debug('[JitsiMeet] token not provided, proceeding without JWT');
      }

      await loadJitsiScript(JITSI_DOMAIN, finalAppIdForScript);

      if (!window.JitsiMeetExternalAPI) {
        throw new Error('Jitsi Meet External API not available after script load');
      }

      const displayName = passedDbUserName || userName || userId || 'ผู้เข้าร่วม';

      updateMeetingInfo({ roomName: simpleRoomNameFromDb, userName: displayName });

      const options = {
        roomName: finalRoomNameForJitsi, 
        width: '100%',
        height: '100%',
        parentNode: jitsiContainerRef.current,
        jwt: finalToken || undefined, 
        userInfo: {
          displayName,
        },
        configOverwrite: {
          prejoinPageEnabled: false,
          startWithAudioMuted: true,
          startWithVideoMuted: true,
          enableWelcomePage: false,
          enableClosePage: false,
        },
        interfaceConfigOverwrite: {
          TILE_VIEW_MAX_COLUMNS: 5,
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
        },
      };

      const api = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, options);
      apiRef.current = api;

      if (typeof onExit === 'function') {
        api.addListener('readyToClose', onExit);
      }
      try { api.addListener('iframeLoadEnd', () => setIsLoading(false)); } catch (_) {}

      const loadingTimeout = setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
          console.warn('Jitsi meeting loading timed out (5s). Displaying content.');
        }
      }, 5000);

      return () => {
        clearTimeout(loadingTimeout);
        if (apiRef.current) {
          try { apiRef.current.dispose(); } catch (_) {}
          apiRef.current = null;
        }
      };
    } catch (error) {
      console.error('[JitsiMeet] Error starting Jitsi meeting:', error);
      setError(`ไม่สามารถเริ่มการประชุมได้: ${error.message}`);
      setIsLoading(false);
      return () => {};
    }
  };

  // ----------------------------------------------------------------
  // Effect หลัก (ไม่เปลี่ยนแปลง)
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!isUserDataLoaded) return;

    setError(null);
    setIsLoading(true);

    if (!meetLink) {
      setIsLoading(false);
      return () => {};
    }

    let roomName = ''; 
    let jwtFromLink = '';
    try {
      const url = new URL(meetLink);
      roomName = url.pathname.replace(/^\//, '');
      if (url.hash && url.hash.startsWith('#')) {
        const params = new URLSearchParams(url.hash.substring(1));
        jwtFromLink = params.get('jwt') || '';
      }
    } catch (e) {
      roomName = meetLink.split('/').pop().split('?')[0];
    }

    if (!roomName) {
      setError('ไม่สามารถแยก Room Name จากลิงก์ที่ให้มาได้');
      setIsLoading(false);
      return () => {};
    }

    let cleanupFn = () => {};
    let cancelled = false;
    startJitsiMeeting(roomName, jwtFromLink, dbUserName, dbModulator).then((fn) => {
      if (!cancelled && typeof fn === 'function') cleanupFn = fn;
    }).catch((e) => {
      console.error('[JitsiMeet] startJitsiMeeting promise rejected', e);
    });

    return () => {
      cancelled = true;
      try { cleanupFn(); } catch (e) { /* no-op */ }
    };
    // [ลบออก] userRole ถูกลบออกจาก dependencies เพราะไม่ถูกใช้แล้ว
  }, [meetLink, userId, jwtToken, dbUserName, dbModulator, userName, isUserDataLoaded]); 

// ----------------------------------------------------------------
// render (ไม่เปลี่ยนแปลง)
// ----------------------------------------------------------------
  return (
    <div className="jitsi-meet-container">
      <div className="meet-header">
        <div className="header-left">
          {isLoading && <div className="loading-spinner small"></div>}
          {error && <div className="error-text" style={{ color: 'crimson' }}>{error}</div>}
        </div>
      </div>

      <div className="meet-main-content">
        <div className="meet-video-section">
          {isLoading && (
            <div className="loading-container">
              <div className="loading-spinner large"></div>
              <h4 className="loading-title">กำลังโหลด...</h4>
              <p className="loading-description">กรุณารอสักครู่ ระบบกำลังเตรียมห้องประชุมให้คุณ</p>
            </div>
          )}

          <div
            ref={jitsiContainerRef}
            className="meet-iframe-container"
            style={{ width: '100%', height: isLoading ? 0 : '100%', visibility: isLoading ? 'hidden' : 'visible' }}
          />
        </div>

        <div className="meet-info-section">
          <div className="info-panel">
            <div className="info-title-container">
              <img src="/Wisdom-Logo.png" alt="Wisdom Logo" className="wisdom-logo" style={{ width: '200px', height: '100px', marginBottom: '8px' }} />
              <h4 className="info-title">คำตอบการแข่งขัน</h4>
            </div>


            {/**************************************/}
            {/* ⬇️  จุดที่แก้ไข ⬇️           */}
            {/**************************************/}
            <QuestionSystem 
              userName={userName || userId}
//=======
//            <QuestionSystem 
//              userName={dbUserName || userName || userId}
//>>>>>>> e220eb1a913186ea6b5ba9e9ca046445bc8ab06f
                roomName={meetingInfo.roomName} 
                  onQuestionChange={() => {}} />

            {showCountdown && (
              <div className="info-item">
                <div className="info-label">การนับเวลา</div>
                <div className="info-value countdown-display">
                  <span className="countdown-number">{countdown}</span>
                  <span className="countdown-text">วินาที</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JitsiMeet;